import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { getFaceLabel, getToothName, type ToothFace } from '../../constants/odontology';
import { getToothFaceLayout, type ToothFill, type ToothView } from '../../utils/dentalChartView';
import ToothLateralGlyph from './ToothLateralGlyph';

/**
 * Um dente do odontograma, em vista oclusal: quatro faces periféricas e a face
 * oclusal/incisal ao centro. Clicar numa face seleciona a face; clicar no
 * número seleciona o dente inteiro.
 *
 * O que já foi executado aparece em cor sólida; o que está planejado aparece
 * hachurado. A distinção por textura sobrevive à impressão em preto e branco e
 * não depende de o leitor diferenciar matizes.
 */

const SIZE = 40;
const INSET = 13;
const OUTSET = SIZE - INSET;

const FACE_SHAPES: Record<'top' | 'bottom' | 'left' | 'right', string> = {
  top: `0,0 ${SIZE},0 ${OUTSET},${INSET} ${INSET},${INSET}`,
  right: `${SIZE},0 ${SIZE},${SIZE} ${OUTSET},${OUTSET} ${OUTSET},${INSET}`,
  bottom: `0,${SIZE} ${SIZE},${SIZE} ${OUTSET},${OUTSET} ${INSET},${OUTSET}`,
  left: `0,0 0,${SIZE} ${INSET},${OUTSET} ${INSET},${INSET}`,
};

const EMPTY_FILL = '#FFFFFF';
const STROKE = '#94A3B8';

/** Status ainda não executados são desenhados com hachura diagonal. */
const HATCHED_STATUSES = new Set(['planned', 'in_progress']);

export interface ToothGlyphProps {
  toothNumber: string;
  view: ToothView;
  /** Faces destacadas na seleção corrente. */
  selectedFaces?: ToothFace[];
  selected?: boolean;
  /** Numeração acima do desenho (arcada superior) ou abaixo (inferior). */
  numberPosition?: 'top' | 'bottom';
  /** Exibe o perfil lateral (raiz, canal, prótese) acima da vista oclusal. */
  showLateral?: boolean;
  disabled?: boolean;
  onSelectTooth?: (toothNumber: string) => void;
  onToggleFace?: (toothNumber: string, face: ToothFace) => void;
}

const ToothGlyph: React.FC<ToothGlyphProps> = ({
  toothNumber,
  view,
  selectedFaces = [],
  selected = false,
  numberPosition = 'top',
  showLateral = false,
  disabled = false,
  onSelectTooth,
  onToggleFace,
}) => {
  const layout = getToothFaceLayout(toothNumber);
  const interactive = !disabled && Boolean(onSelectTooth || onToggleFace);

  const fillFor = (face: ToothFace): ToothFill | null => view.faceFills[face] || view.toothFill;

  // Um pattern por cor hachurada presente neste dente. O id carrega o número do
  // dente porque ids de SVG são globais no documento.
  const hatchColors = useMemo(() => {
    const colors = new Set<string>();
    const collect = (fill: ToothFill | null | undefined) => {
      if (fill && HATCHED_STATUSES.has(fill.status)) colors.add(fill.color);
    };
    Object.values(view.faceFills).forEach(collect);
    collect(view.toothFill);
    return Array.from(colors);
  }, [view.faceFills, view.toothFill]);

  const hatchId = (color: string) => `hatch-${toothNumber}-${color.replace('#', '')}`;

  const paintFor = (face: ToothFace): string => {
    const fill = fillFor(face);
    if (!fill) return EMPTY_FILL;
    return HATCHED_STATUSES.has(fill.status) ? `url(#${hatchId(fill.color)})` : fill.color;
  };

  const isFaceSelected = (face: ToothFace) => selectedFaces.includes(face);

  const handleFaceClick = (event: React.MouseEvent, face: ToothFace) => {
    if (disabled) return;
    event.stopPropagation();
    if (onToggleFace) onToggleFace(toothNumber, face);
    else onSelectTooth?.(toothNumber);
  };

  const renderFace = (position: 'top' | 'bottom' | 'left' | 'right') => {
    const face = layout[position];
    return (
      <polygon
        key={position}
        points={FACE_SHAPES[position]}
        fill={paintFor(face)}
        stroke={isFaceSelected(face) ? '#0F172A' : STROKE}
        strokeWidth={isFaceSelected(face) ? 2 : 0.75}
        onClick={(e) => handleFaceClick(e, face)}
        style={{ cursor: interactive ? 'pointer' : 'default' }}
      >
        <title>{getFaceLabel(face, toothNumber)}</title>
      </polygon>
    );
  };

  const numberLabel = (
    <Typography
      component="span"
      sx={{
        fontSize: '0.68rem',
        fontWeight: selected ? 800 : 600,
        color: selected ? '#0F172A' : '#64748B',
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
      }}
    >
      {toothNumber}
    </Typography>
  );

  const tooltipTitle = [
    `${toothNumber} — ${getToothName(toothNumber)}`,
    view.entries.length ? `${view.entries.length} lançamento(s)` : 'Sem lançamentos',
  ].join(' · ');

  return (
    <Tooltip title={tooltipTitle} arrow enterDelay={400}>
      <Box
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={`Dente ${toothNumber}, ${getToothName(toothNumber)}`}
        aria-pressed={interactive ? selected : undefined}
        onClick={() => !disabled && onSelectTooth?.(toothNumber)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectTooth?.(toothNumber);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: numberPosition === 'top' ? 'column' : 'column-reverse',
          alignItems: 'center',
          gap: 0.25,
          px: 0.25,
          py: 0.25,
          borderRadius: 1,
          bgcolor: selected ? '#E0F2FE' : 'transparent',
          outline: selected ? '2px solid #0284C7' : 'none',
          outlineOffset: -1,
          cursor: interactive ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
          '&:hover': interactive ? { bgcolor: selected ? '#E0F2FE' : '#F1F5F9' } : undefined,
          '&:focus-visible': { outline: '2px solid #0284C7' },
        }}
      >
        {numberLabel}

        {/* Perfil lateral. Em `column-reverse` (arcada inferior) a ordem do DOM
            se inverte sozinha, deixando as faces oclusais das duas arcadas
            voltadas uma para a outra, como numa oclusão. */}
        {showLateral && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', overflow: 'visible' }}>
            <ToothLateralGlyph
              toothNumber={toothNumber}
              view={view}
              selected={selected}
              disabled={disabled}
              onSelectTooth={onSelectTooth}
            />
          </Box>
        )}

        <Box
          component="svg"
          viewBox={`-1 -1 ${SIZE + 2} ${SIZE + 2}`}
          sx={{ width: { xs: 26, sm: 30, md: 32 }, height: { xs: 26, sm: 30, md: 32 }, display: 'block' }}
        >
          {hatchColors.length > 0 && (
            <defs>
              {hatchColors.map((color) => (
                <pattern
                  key={color}
                  id={hatchId(color)}
                  patternUnits="userSpaceOnUse"
                  width="5"
                  height="5"
                  patternTransform="rotate(45)"
                >
                  <rect width="5" height="5" fill="#FFFFFF" />
                  <line x1="0" y1="0" x2="0" y2="5" stroke={color} strokeWidth="2.5" />
                </pattern>
              ))}
            </defs>
          )}

          {renderFace('top')}
          {renderFace('right')}
          {renderFace('bottom')}
          {renderFace('left')}
          <rect
            x={INSET}
            y={INSET}
            width={SIZE - INSET * 2}
            height={SIZE - INSET * 2}
            fill={paintFor(layout.center)}
            stroke={isFaceSelected(layout.center) ? '#0F172A' : STROKE}
            strokeWidth={isFaceSelected(layout.center) ? 2 : 0.75}
            onClick={(e) => handleFaceClick(e, layout.center)}
            style={{ cursor: interactive ? 'pointer' : 'default' }}
          >
            <title>{getFaceLabel(layout.center, toothNumber)}</title>
          </rect>

          {view.absent && (
            <g pointerEvents="none">
              <line x1={2} y1={2} x2={SIZE - 2} y2={SIZE - 2} stroke="#475569" strokeWidth={2.5} strokeLinecap="round" />
              <line x1={SIZE - 2} y1={2} x2={2} y2={SIZE - 2} stroke="#475569" strokeWidth={2.5} strokeLinecap="round" />
            </g>
          )}
        </Box>

        {/* Marcadores de status: âmbar = a realizar, verde = realizado. */}
        <Box sx={{ display: 'flex', gap: 0.25, height: 5, alignItems: 'center' }}>
          {view.counts.planned > 0 && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#F59E0B' }} />}
          {view.counts.completed > 0 && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#16A34A' }} />}
          {view.counts.in_progress > 0 && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#2563EB' }} />}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ToothGlyph;
